import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Token } from './models/token.model';
import { UsuarioService } from '../services/usuario.service';
import { Authority } from './models/authority.model';
import { NgxPermissionsService } from 'ngx-permissions';
import { Usuario } from '../models/usuario.model';

const baseUrl = `${environment.URL_API}/`;
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authenticated: boolean = false;
  private _token: BehaviorSubject<Token> = new BehaviorSubject(null);

  constructor(
    private _httpClient: HttpClient,
    private _router: Router,
    private _usuarioService: UsuarioService,
    private _permissionsService: NgxPermissionsService
  ) {
    if (localStorage.getItem('token')) {
      this._token.next(JSON.parse(localStorage.getItem('token')));
    }
  }

  set token(value: Token) {
    localStorage.removeItem('token');
    this._token.next(value);

    if (value != null) {
      localStorage.setItem('token', JSON.stringify(value));
    }
  }

  get token$(): Observable<Token> {
    return this._token.asObservable();
  }

  get accessToken(): string {
    return localStorage.getItem('token')
      ? JSON.parse(localStorage.getItem('token')).access_token
      : '';
  }

  get cpfCnpj(): string {
    return localStorage.getItem('token')
      ? JSON.parse(localStorage.getItem('token')).cpfCnpj
      : '';
  }

  get isAuthenticated(): boolean {
    return this._authenticated;
  }

  signIn(credentials: any): Observable<any> {
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    const optionsToken = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/x-www-form-urlencoded')
        .set('Authorization', `Basic ${environment.AUTH_TOKEN}`),
      params: new HttpParams()
        .set('username', credentials.username)
        .set('password', credentials.password)
        .set('grant_type', 'password'),
    };
    return this._httpClient
      .post(baseUrl + 'oauth/token', credentials, optionsToken)
      .pipe(
        switchMap((response: any) => {
          if (response.access_token) {
            this.token = { cpfCnpj: credentials.username, ...response };
            this._authenticated = true;
          }
          return of(response);
        })
      );
  }

  signInUsingToken(): Observable<any> {
    return this._httpClient.get(baseUrl + 'userinfo').pipe(
      catchError(() => {
        return of(false);
      }),
      switchMap((response: any) => {
        if (response) {
          this._authenticated = true;

          this._usuarioService.usuario$
            .pipe(take(1))
            .subscribe((usuario: Usuario) => {
              this._usuarioService.usuario = {
                ...usuario,
                nome: response.name,
                cpfCnpj: this.cpfCnpj,
              };
            });

          return this.getAuthority();
        } else {
          this._authenticated = false;
          this.token = null;
          return of(false);
        }
      })
    );
  }

  getAuthority(): Observable<any> {
    return this._httpClient.get(baseUrl + 'security/currentuser/profiles').pipe(
      catchError(() => {
        return of(false);
      }),
      switchMap((response: any) => {
        if (response) {
          var permissions = response.map(function (authority: Authority) {
            return authority['authority'];
          });

          this._permissionsService.loadPermissions(permissions);
          return of(true);
        } else {
          return of(false);
        }
      })
    );
  }

  signOut(): Observable<any> {
    return this._httpClient.get(baseUrl + 'logout').pipe(
      catchError(() => {
        return of(false);
      }),
      switchMap((response: any) => {
        this.token = null;
        this._authenticated = false;
        this._router.navigate(['/admin/identificacao']);
        return of(true);
      })
    );
  }

  logout() {
    this.token = null;
    this._authenticated = false;
  }

  check(): Observable<boolean> {
    if (this._authenticated) {
      return of(true);
    }

    if (!this.accessToken) {
      return of(false);
    }

    return this.signInUsingToken();
  }
}
